import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ChevronRight } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Book a Ride', path: '/book' },
    { name: 'Contact', path: '/contact' },
    { name: 'Customer Portal', path: '/portal' },
  ];

  const services = [
    { name: 'Airport Transfers', path: '/services#airport-transfers' },
    { name: 'Hourly Chauffeur', path: '/services#hourly-service' },
    { name: 'City to City', path: '/services#city-to-city' },
    { name: 'Event Transportation', path: '/services#event-transportation' },
  ];

  return (
    <footer data-testid="main-footer" className="bg-[#050505] border-t border-white/5">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <div className="flex flex-col">
                <span className="font-serif text-2xl text-white tracking-wide">
                  Capital
                </span>
                <span className="text-xs text-[#D4AF37] tracking-[0.2em] uppercase -mt-1">
                  Chauffeur Services
                </span>
              </div>
            </Link>
            <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
              Premium luxury transportation for executives, government officials, 
              and discerning travelers in the Washington DC metropolitan area.
            </p>
            <div className="flex items-center gap-2 text-sm text-[#A3A3A3]">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              <span>Washington DC | Northern Virginia | Maryland</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-white text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group"
                  >
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-serif text-white text-lg mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.path}
                    className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2 group"
                  >
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-white text-lg mb-6">Contact Us</h4>
            <div className="space-y-4">
              <a
                href="tel:+12026840534"
                data-testid="footer-phone"
                className="flex items-start gap-3 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors group"
              >
                <Phone className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                <div>
                  <span className="block text-xs text-[#666] mb-1">Call Us</span>
                  <span className="text-white group-hover:text-[#D4AF37] transition-colors">
                    +1 (202) 684-0534
                  </span>
                </div>
              </a>
              <a
                href="mailto:contact@capitalchauffeurservices.com"
                data-testid="footer-email"
                className="flex items-start gap-3 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors group"
              >
                <Mail className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                <div>
                  <span className="block text-xs text-[#666] mb-1">Email Us</span>
                  <span className="text-white group-hover:text-[#D4AF37] transition-colors text-sm">
                    contact@capitalchauffeurservices.com
                  </span>
                </div>
              </a>
            </div>

            {/* Available 24/7 Badge */}
            <div className="mt-8 px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/20">
              <span className="text-xs text-[#D4AF37] tracking-wider uppercase">
                Available 24/7
              </span>
              <p className="text-white text-sm mt-1">
                For your convenience
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#666] text-xs">
              © {currentYear} Capital Chauffeur Services. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-[#666] hover:text-[#D4AF37] text-xs transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-[#666] hover:text-[#D4AF37] text-xs transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
